import { useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useChartColors } from './chartPalette';
import { ChartTooltip } from './ChartTooltip';
import {
  CHART_CHAR_PX,
  deriveAxisFormatter,
  estimateTextWidth,
  truncateLabel,
} from './chartFormat';
import { formatNumber } from '@/utils';

export interface SingleSeriesBarDatum {
  label: string;
  value: number;
}

interface SingleSeriesBarChartProps {
  data: SingleSeriesBarDatum[];
  /** Fixed plot height. Omitted, the chart grows with its row count so ten
   * insurers get the same bar thickness as three. */
  height?: number;
  valueFormatter?: (value: number) => string;
  /** Numeric-axis tick formatter. Defaults to a compact form DERIVED from
   * `valueFormatter` (money -> "₹2L", counts -> "20K"). */
  axisFormatter?: (value: number) => string;
  /** Empty-state copy when `data` is empty. */
  emptyMessage?: string;
}

// U-02 layout bounds, in px. The category axis is sized to the longest label
// between these; anything longer is truncated with its full text in a <title>.
const LABEL_AXIS_MIN = 48;
const LABEL_AXIS_MAX = 180;
const VALUE_LABEL_MIN = 36;
const VALUE_LABEL_MAX = 140;
const ROW_PX = 30;
// Category labels are sized and truncated against a slightly wider glyph than
// the average: a capitals-heavy name ("HDFC ERGO") runs wide and would
// otherwise be clipped at the card's left edge.
const LABEL_CHAR_PX = CHART_CHAR_PX + 0.8;

/** Sizes the chart's two text gutters from the data it will draw. On a
 * narrow card (`containerWidth` known) the label axis never takes more than
 * about a third of it, so the bars keep most of the room. */
export function barChartLayout(
  data: SingleSeriesBarDatum[],
  valueFormatter: (value: number) => string,
  containerWidth?: number
) {
  const longestLabel = Math.max(0, ...data.map((d) => estimateTextWidth(d.label, LABEL_CHAR_PX)));
  const cap = containerWidth
    ? Math.max(LABEL_AXIS_MIN, Math.min(LABEL_AXIS_MAX, Math.floor(containerWidth * 0.35)))
    : LABEL_AXIS_MAX;
  const labelAxisWidth = Math.min(cap, Math.max(LABEL_AXIS_MIN, longestLabel + 12));
  const longestValue = Math.max(0, ...data.map((d) => estimateTextWidth(valueFormatter(d.value), 6.5)));
  return {
    labelAxisWidth,
    labelMaxChars: Math.floor((labelAxisWidth - 12) / LABEL_CHAR_PX),
    rightMargin: Math.min(VALUE_LABEL_MAX, Math.max(VALUE_LABEL_MIN, longestValue + 12)),
    autoHeight: Math.max(120, data.length * ROW_PX + 40),
  };
}

/** Category-axis tick: the (possibly truncated) label, full text on hover. */
export function CategoryTick({
  x,
  y,
  payload,
  maxChars,
  fill,
}: {
  x?: number;
  y?: number;
  payload?: { value: string };
  maxChars: number;
  fill: string;
}) {
  const full = String(payload?.value ?? '');
  const shown = truncateLabel(full, maxChars);
  return (
    <g transform={`translate(${x ?? 0},${y ?? 0})`}>
      <text x={-4} y={0} dy={4} textAnchor="end" fill={fill} fontSize={12}>
        {shown !== full && <title>{full}</title>}
        {shown}
      </text>
    </g>
  );
}

/**
 * Horizontal single-hue bar chart for a "compare magnitude across
 * categories" job (dataviz skill: sequential color, one hue, no legend --
 * "a single series needs no legend box"). Used for "Policies by Insurer" and
 * "Claims by Status": both are one measure (a count) across a handful of
 * named categories, some with long labels, hence horizontal bars.
 *
 * U-02: the label axis and the right-hand value-label gutter are sized from
 * the data (capped), so a long insurer name no longer wraps over its
 * neighbour and a wide "₹12,34,567" label is no longer clipped; the numeric
 * axis only ticks whole numbers, in a compact form.
 */
export function SingleSeriesBarChart({
  data,
  height,
  valueFormatter = (v) => formatNumber(v),
  axisFormatter,
  emptyMessage = 'No data available for the selected period.',
}: SingleSeriesBarChartProps) {
  const colors = useChartColors();
  const [containerWidth, setContainerWidth] = useState<number | undefined>(undefined);

  if (data.length === 0 || data.every((d) => !(d.value > 0))) {
    return <p className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center">{emptyMessage}</p>;
  }

  const tickFormatter = axisFormatter ?? deriveAxisFormatter(valueFormatter);
  const layout = barChartLayout(data, valueFormatter, containerWidth);

  return (
    <ResponsiveContainer
      width="100%"
      height={height ?? layout.autoHeight}
      onResize={(width) => setContainerWidth(width)}
    >
      <BarChart
        data={data}
        layout="vertical"
        margin={{ top: 4, right: layout.rightMargin, bottom: 4, left: 4 }}
        barCategoryGap={8}
      >
        <CartesianGrid horizontal={false} stroke={colors.grid} strokeDasharray="0" />
        <XAxis
          type="number"
          allowDecimals={false}
          tickFormatter={(v: number) => tickFormatter(Number(v))}
          tick={{ fill: colors.ink.muted, fontSize: 12 }}
          axisLine={{ stroke: colors.axis }}
          tickLine={false}
        />
        <YAxis
          type="category"
          dataKey="label"
          width={layout.labelAxisWidth}
          interval={0}
          tick={<CategoryTick maxChars={layout.labelMaxChars} fill={colors.ink.secondary} />}
          axisLine={{ stroke: colors.axis }}
          tickLine={false}
        />
        <Tooltip
          cursor={{ fill: colors.grid, opacity: 0.5 }}
          content={({ active, payload }) => (
            <ChartTooltip
              active={active}
              rows={
                payload && payload.length > 0
                  ? [
                      {
                        label: String(payload[0].payload.label),
                        value: valueFormatter(Number(payload[0].value)),
                        color: colors.sequential,
                      },
                    ]
                  : []
              }
            />
          )}
        />
        <Bar dataKey="value" fill={colors.sequential} radius={[0, 4, 4, 0]} maxBarSize={22}>
          {/* A plain <text>, not recharts' default label: that one word-wraps
              to the bar's box, so "12 policies" beside a short bar broke over
              two lines. The right margin above is sized to hold it. */}
          <LabelList
            dataKey="value"
            content={(props) => {
              const x = Number(props.x ?? 0) + Number(props.width ?? 0) + 5;
              const y = Number(props.y ?? 0) + Number(props.height ?? 0) / 2;
              return (
                <text
                  x={x}
                  y={y}
                  dy={4}
                  fill={colors.ink.secondary}
                  fontSize={11}
                  className="recharts-bar-value-label"
                >
                  {valueFormatter(Number(props.value))}
                </text>
              );
            }}
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
